import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
    Box,
    Button,
    Stack,
    SuccessAlert,
    ErrorAlert,
    Link,
    CautionAlert,
    Input,
} from '@99designs/ui';
import { DividerWithText, useSnackbar } from '@99designs/purgatory';
import Bugsnag from '@bugsnag/js';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n';
import { Source } from '../../common/constants';
import is99StaffEmail from '../../common/is99StaffEmail';
import { linkTo } from '../../common/linkTo';
import { partitionArray } from '../../common/partitionArray';
import { PageType } from '../../common/types';
import { BackendErrorType, getErrorForField } from '../../common/utils';
import { useAuthConfigContext } from '../AuthConfigContext';
import { AuthLink } from '../AuthLink';
import { Captcha } from '../Captcha';
import { Checkbox, PasswordField } from '../Form';
import { Layout } from '../Layout';
import { OAuthContinueButton } from '../OAuthContinueButton';

export interface LoginProps {
    clientError?: string;
    onSubmit: (
        endpoint: 'login' | 'signup',
        data: Record<string, unknown>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) => Promise<{ status: number; body: any }>;
    resetSuccessReason?: string;
    setPage: (page: PageType, link: string) => void;
    showRecaptcha?: boolean;
    source: Source;
    target?: string;
}

interface FormState {
    email: string;
    password: string;
    rememberMe: string;
}

const resetSuccessMessageMap: Record<string, string> = {
    'password-reset': __('Password updated. Please log in to continue.'),
    'email-updated': __('Email updated. Please log in to continue.'),
};

function ClientAlert({ alertCode, onDismiss }: { alertCode: string; onDismiss: () => void }) {
    const alertMessageMap: Record<
        string,
        { message: ReactNode; component: typeof ErrorAlert | typeof CautionAlert }
    > = {
        'oauth-failed': {
            message: __('Could not connect. Try again or log in with your email.'),
            component: ErrorAlert,
        },
        'oauth-account-creation': {
            message: (
                <FormattedReactMessage
                    message={__(
                        'Your account could not be created. Please contact <Link>Customer Support</Link> for assistance.'
                    )}
                    replacements={{
                        Link: <Link href={linkTo.helpContact()} color="bleachedAs" />,
                    }}
                />
            ),
            component: ErrorAlert,
        },
        'oauth-password': {
            message: __(
                'It looks like you have an existing 99designs account. Please log in with your email.'
            ),
            component: CautionAlert,
        },
        geo: {
            message: __(`Registrations aren't available in your country`),
            component: ErrorAlert,
        },
        'unknown-error': {
            message: __('An unknown error has occurred. Try again or log in with your email.'),
            component: ErrorAlert,
        },
        'cant-login': {
            message: __("Sorry, you can't log in at this time."),
            component: ErrorAlert,
        },
    };

    const alert = alertMessageMap[alertCode];

    return alert ? (
        <alert.component onDismiss={onDismiss} data-testid="error-message">
            {alertMessageMap[alertCode].message}
        </alert.component>
    ) : null;
}

export function Login({
    clientError,
    onSubmit,
    resetSuccessReason,
    setPage,
    showRecaptcha = true,
    source,
    target,
}: LoginProps) {
    const { recaptchaSiteKey } = useAuthConfigContext();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const recaptchaRef = useRef<any>(null);
    const [authError, setAuthError] = useState('');
    const [showClientError, setShowClientError] = useState(true);
    const [recaptchaError, setRecaptchaError] = useState('');
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const [force2FA, setForce2FA] = useState<boolean>(false);
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isSubmitting },
        getValues,
    } = useForm<FormState>({
        defaultValues: {
            email: '',
        },
    });

    const { ref: rhfEmailRef, ...rhfEmailProps } = register('email', {
        required: __('Enter your email address'),
    });
    const emailRef = useRef<HTMLInputElement | null>();
    useEffect(() => {
        if (emailRef.current) {
            emailRef.current.focus();
        }
    }, []);

    const snackbar = useSnackbar();

    const showOktaButton = is99StaffEmail(watch('email'));

    function handleRecaptchaChange(token: string) {
        setRecaptchaError('');
        setRecaptchaToken(token);
    }

    function handleRecaptchaError() {
        // Force 2FA if recaptcha fails to load
        setForce2FA(true);
    }

    function handleDismissError() {
        setShowClientError(false);
        setAuthError('');
    }

    function submitForm({ email, password, rememberMe }: FormState) {
        const needsRecaptcha = !force2FA && showRecaptcha && !recaptchaToken;
        if (recaptchaSiteKey && needsRecaptcha) {
            setRecaptchaError(__("Confirm that you're not a robot"));
            return;
        }

        return onSubmit('login', {
            email,
            password,
            rememberMe,
            recaptchaToken,
            force_2fa: force2FA,
        })
            .then((d) => {
                if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                    setRecaptchaToken(null);
                }

                if (d.body.errors) {
                    const errors = partitionArray(d.body.errors, (error: BackendErrorType) => {
                        return Object.keys(getValues()).includes(error.param);
                    });

                    if (errors.matches.length > 0) {
                        setAuthError(errors.matches[0].message);
                    }

                    errors.nonMatches.forEach((item: BackendErrorType) => {
                        switch (item.param) {
                            case 'captcha':
                                setRecaptchaError(item.message);
                                break;
                            default:
                                snackbar.error(__('Something went wrong, please try again later.'));
                                Bugsnag.notify(`${item.param} : ${item.message}`, (event) => {
                                    event.context = 'Logging in';
                                });
                                break;
                        }
                    });
                }
            })
            .catch((e) => {
                Bugsnag.notify(e, (event) => {
                    event.context = 'Logging in';
                });
            });
    }

    return (
        <Layout
            title={__('Log in')}
            description={__('Log in to your 99designs account')}
            heading={__('Log in')}
        >
            <form onSubmit={handleSubmit(submitForm)}>
                <Stack space="space8">
                    {resetSuccessReason && resetSuccessMessageMap[resetSuccessReason] && (
                        <SuccessAlert data-testid="reset-success-message">
                            {resetSuccessMessageMap[resetSuccessReason]}
                        </SuccessAlert>
                    )}
                    {authError ? (
                        <ErrorAlert onDismiss={handleDismissError}>{authError}</ErrorAlert>
                    ) : showClientError && clientError ? (
                        <ClientAlert alertCode={clientError} onDismiss={handleDismissError} />
                    ) : null}

                    <Input
                        {...rhfEmailProps}
                        error={getErrorForField('email', errors)}
                        id="email"
                        name="email"
                        type="text"
                        label={__('Email')}
                        ref={(el) => {
                            rhfEmailRef(el);
                            emailRef.current = el;
                        }}
                        autoFocus
                        autoComplete="username"
                    />

                    <PasswordField
                        {...register('password', { required: __('Enter your password') })}
                        error={getErrorForField('password', errors)}
                        id="password"
                        label={__('Password')}
                        autoComplete="current-password"
                    />

                    <Box display="flex" justifyContent="space-between">
                        <Checkbox {...register('rememberMe')} id="remember_me">
                            <label htmlFor="remember_me">{__('Remember me')}</label>
                        </Checkbox>
                        <Box whiteSpace="nowrap">
                            <Link href={linkTo.forgotPassword()}>{__('Forgot password?')}</Link>
                        </Box>
                    </Box>

                    {showRecaptcha && !!recaptchaSiteKey && (
                        <Captcha
                            ref={recaptchaRef}
                            siteKey={recaptchaSiteKey}
                            onChange={handleRecaptchaChange}
                            onErrored={handleRecaptchaError}
                            error={recaptchaError}
                        />
                    )}

                    <Button
                        data-testid="login-button"
                        name="login-button"
                        loading={isSubmitting}
                        size="large"
                        appearance="primary"
                        width="fill"
                        type="submit"
                    >
                        {__('Log in')}
                    </Button>

                    <FormattedReactMessage
                        message={__("Don't have an account? <Link>Sign up</Link>")}
                        replacements={{
                            Link: (
                                <AuthLink
                                    data-testid="signup-link"
                                    href={linkTo.signup(undefined, target)}
                                    page="signup"
                                    setPage={setPage}
                                />
                            ),
                        }}
                    />

                    <DividerWithText text={__('OR')} />

                    <OAuthContinueButton
                        target={target}
                        source={source}
                        action="login"
                        provider="Vistaprint"
                    />

                    {showOktaButton && (
                        <Button
                            href={linkTo.authSAMLRedirect(target)}
                            data-testid="okta-button"
                            size="large"
                            appearance="secondary"
                            width="fill"
                        >
                            Log in via Okta
                        </Button>
                    )}
                </Stack>
            </form>
        </Layout>
    );
}
